/**
 * 时间格式化工具
 *
 * 移植自 desktop-web 的 timezoneFormatter，仅保留订单查询所需的
 * 「后端 UTC → 浏览器本地时区展示」能力；两个项目无共享包，故此处为移植而非引用。
 * 仅做展示格式化，不涉及筛选条件的 UTC 转换——契约已确定为「前端下发客户端本地日期
 * （YYYY-MM-DD），后端凭 X-Timezone 请求头换算成 UTC 时刻闭区间」，见 swiftx-customer-service
 * OrderQueryServiceImpl.resolveOrderTimeRange。故此处无需、也不应再补筛选侧的转换。
 */

/**
 * 无时区后缀时按 UTC 解析。
 *
 * 客户门户当前的时间字段由后端统一以 `Instant.toString()` 下发、均带 Z 后缀（见
 * swiftx-customer-service OrderQueryServiceImpl.format），故本函数是防御性兜底：
 * 一旦上游改成 "2026-02-04 08:13:21" 这类无后缀字符串，JS `new Date()` 会按浏览器本地时区
 * 解析，导致后续时区转换少转一次。因此对既无 Z、也无 ±HH:MM 偏移的字符串，补成 ISO UTC 形式再解析。
 */
export const normalizeUtcString = (s: string): string => {
    const t = s.trim();
    if (/Z$/i.test(t) || /[+-]\d{2}:?\d{2}$/.test(t)) return t;
    return t.replace(' ', 'T') + 'Z';
};

/** 取当前浏览器所在 IANA 时区，环境不支持时兜底 UTC */
export const getBrowserTimeZone = (): string => {
    return typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        : 'UTC';
};

/** 将 Date 格式化为「浏览器本地时区」的 YYYY-MM-DD HH:mm:ss；非法日期返回 '-' */
const formatDateToLocal = (date: Date): string => {
    if (isNaN(date.getTime())) return '-';

    try {
        /* 用 formatToParts 手动拼接，规避不同 locale 的默认分隔符差异，保证输出为横杠格式 */
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: getBrowserTimeZone(),
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).formatToParts(date);

        const pick = (type: string): string => parts.find((p) => p.type === type)?.value || '';
        const hour = pick('hour') === '24' ? '00' : pick('hour');
        return `${pick('year')}-${pick('month')}-${pick('day')} ${hour}:${pick('minute')}:${pick('second')}`;
    } catch {
        return '-';
    }
};

/**
 * 将后端 UTC 时间字符串格式化为「浏览器本地时区」的 YYYY-MM-DD HH:mm:ss。
 *
 * 空值、非法时间统一返回 '-'，供表格/详情页直接渲染。
 */
export const formatUtcToLocal = (utcTimeString: string | undefined | null): string => {
    if (!utcTimeString) return '-';
    return formatDateToLocal(new Date(normalizeUtcString(utcTimeString)));
};

/**
 * 将 "GMT±HH:MM" 解析为 ISO 时区偏移 "±HH:MM"；格式不符返回 null。
 *
 * 承运商原始轨迹数据里小时位既有两位（"GMT-07:00"）也有一位（"GMT-6:00"，见 api-server
 * TrackingEvent 字段注释），一位时补零后再拼成 ISO 偏移；无法识别的格式打警告日志便于定位
 * 上游数据格式变化，避免静默按 UTC 解析导致展示时间整体偏移数小时。
 */
const parseGmtOffset = (gmtOffset: string | undefined | null): string | null => {
    if (!gmtOffset) return null;
    const m = /^GMT([+-])(\d{1,2}):?(\d{2})$/i.exec(gmtOffset.trim());
    if (!m) {
        console.warn('Unrecognized localGmtOffset, fallback to UTC:', gmtOffset);
        return null;
    }
    return `${m[1]}${m[2].padStart(2, '0')}:${m[3]}`;
};

/**
 * 将外部轨迹事件的「操作地本地时间 + 操作地时区偏移」换算为「浏览器本地时区」的
 * YYYY-MM-DD HH:mm:ss 展示。
 *
 * 后端 `localTime`（如 "2026-02-04 08:13:21"）本身不带时区信息，须搭配 `localGmtOffset`
 * （如 "GMT-07:00"）才能还原为绝对时刻；`localGmtOffset` 缺失或格式不识别时按 UTC 降级。
 * 空值、无法解析的时间统一返回 '-'。
 */
export const formatTrackingEventTime = (
    localTime: string | undefined | null,
    localGmtOffset: string | undefined | null
): string => {
    if (!localTime) return '-';

    const raw = localTime.trim();
    const offset = parseGmtOffset(localGmtOffset) ?? 'Z';
    const withOffset = new Date(raw.replace(' ', 'T') + offset);
    if (!isNaN(withOffset.getTime())) return formatDateToLocal(withOffset);

    /*
     * 拼接偏移后解析不出来，多半是上游 localTime 的格式变了、本身已自带时区后缀
     * （"2026-02-04T08:13:21Z" 拼上 "-07:00" 就成了非法串）。这种情况退一步按原串解析：
     * 成功说明它本就是个完整时刻，展示它远好过整列时间静默变成 '-'。
     * 两次都失败才是真的脏数据，此时才返回 '-'。
     */
    const withoutOffset = new Date(raw);
    if (!isNaN(withoutOffset.getTime())) {
        console.warn('localTime already carries timezone info, ignore localGmtOffset:', localTime, localGmtOffset);
        return formatDateToLocal(withoutOffset);
    }
    return '-';
};
